import React, { useContext, useState, useEffect } from 'react';
import { recordImpressionByName } from '@JohnLewisPartnership/experiments-utils';
import PropTypes from 'prop-types';
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../scaffold/UserContext';
import { DesktopMenu } from './desktop-menu/DesktopMenu';
import { getNavigation } from '../../../client/fetch/get-navigation';
import {
  addMediaQueryListener,
  isTabletPortrait,
} from '../../utils/device/identifyDevice';
import { SEOLinks } from './seo-links/SEOLinks';
import { isMonetateGroup } from '../../utils/monetate/isMonetateGroup';
import { MobileMenu } from './mobile-menu/MobileMenu';
import { MOBILE_MI } from '../../utils/constants';

export const fetchChildCategories =
  (queryParams, subset, exp) =>
  ({ id }) =>
    getNavigation({
      id,
      depth: 4,
      subset,
      queryParams,
      ...(exp ? { exp } : {}),
    });

export const Navigation = ({ mobileNavShown, typeaheadIsOpen = false }) => {
  const {
    categories,
    envVars: { monetate },
    features: { monetateExperiences, displaySEOLinks },
    headerClientConfigQueryParameters,
    columnHighlighted: isColumnHighlightedActive,
    mobileBoostWishlist: isMobileFooterWishlistActive,
  } = useContext(EnvVarsFeaturesContext);
  const {
    userState: {
      user: { monetateId },
    },
  } = useContext(UserContext);
  const [flyoutCategories, setFlyoutCategories] = useState({});
  const [shouldRenderMobileMenu, setShouldRenderMobileMenu] = useState(false);

  const isTabbedNavActive = isMonetateGroup(
    { monetateExperiences },
    'tabbed-nav-design'
  );

  const menus = [
    {
      id: 'shop-by-department',
      name: 'Shop by department',
      child: categories.child,
    },
  ];

  const subset = `MOBILE_WEB,FLYOUT`;

  const exp =
    (typeof window !== 'undefined' && window?.__PRELOADED_STATE__?.exp) || '';

  const fetch = fetchChildCategories(
    headerClientConfigQueryParameters,
    subset,
    exp
  );

  const fetchFlyoutCategories = async () => {
    const navCategories = await getNavigation({
      subset: 'MOBILE_WEB,FLYOUT',
      queryParams: headerClientConfigQueryParameters,
    });
    setFlyoutCategories(navCategories);
  };

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      isTabletPortrait() &&
      !flyoutCategories?.child
    ) {
      const cleanup = addMediaQueryListener(fetchFlyoutCategories);
      return cleanup; // Clean up on component unmount
    }
    return () => {}; // Empty cleanup if "if" conditions not met
  }, []);

  useEffect(() => {
    if (mobileNavShown) {
      recordImpressionByName(
        monetate.channel,
        monetateExperiences,
        MOBILE_MI,
        monetateId
      );
    }
  }, [mobileNavShown]);

  // Handle immediate showing and delayed hiding for animation
  useEffect(() => {
    if (mobileNavShown) {
      setShouldRenderMobileMenu(true);
    }
  }, [mobileNavShown]);

  const handleAnimationComplete = () => {
    if (!mobileNavShown) {
      setShouldRenderMobileMenu(false);
    }
  };

  if (shouldRenderMobileMenu) {
    return (
      <MobileMenu
        menus={menus}
        fetchChildCategories={fetch}
        isMobileFooterWishlistActive={isMobileFooterWishlistActive}
        typeaheadIsOpen={typeaheadIsOpen}
        isVisible={mobileNavShown}
        onAnimationComplete={handleAnimationComplete}
      />
    );
  }

  return (
    <>
      <DesktopMenu
        isTabbedNavActive={isTabbedNavActive}
        isColumnHighlightedActive={isColumnHighlightedActive}
        child={
          flyoutCategories.child ? flyoutCategories.child : categories.child
        }
      />
      {displaySEOLinks && <SEOLinks categories={categories.child} />}
    </>
  );
};

Navigation.propTypes = {
  mobileNavShown: PropTypes.bool.isRequired,
  typeaheadIsOpen: PropTypes.bool,
};
